import { ref, onBeforeUnmount } from 'vue'
import { PANEL_CONFIG } from './usePanels'

export function usePanelPerformance() {
  // Pre-computed transform lookup table for common positions
  const TRANSFORM_LOOKUP = new Map<number, string>()

  // Initialize lookup table with common values
  function initializeTransformLookup() {
    PANEL_CONFIG.COMMON_POSITIONS.forEach(pos => {
      TRANSFORM_LOOKUP.set(pos, `translateX(${pos}%) translateZ(0)`)
    })
  }

  // Initialize lookup table
  initializeTransformLookup()

  // Optimized transform using lookup table with fallback
  function getDynamicTransform(dynamicPosition: number | null, isPreview: boolean): string | null {
    if (!isPreview || dynamicPosition === null) {
      return null
    }
    
    // Try lookup table first (most common case)
    const cachedTransform = TRANSFORM_LOOKUP.get(dynamicPosition)
    if (cachedTransform) {
      return cachedTransform
    }
    
    // Fallback for edge cases (less common)
    return `translateX(${dynamicPosition}%) translateZ(0)`
  }

  // ResizeObserver for element-specific resize detection
  const resizeObserver = ref<ResizeObserver | null>(null)

  // IntersectionObserver for off-screen detection
  const intersectionObserver = ref<IntersectionObserver | null>(null)
  const isPanelOnScreen = ref(true)

  // Initialize ResizeObserver for efficient element-specific resize detection
  function initializeResizeObserver(
    panelTab: HTMLElement | null,
    panelContent: HTMLElement | null,
    onResize: () => void
  ) {
    if (!panelTab) return
    
    resizeObserver.value = new ResizeObserver((entries) => {
      // Only process if panel is on screen
      if (!isPanelOnScreen.value) return
      
      for (const entry of entries) {
        if (entry.target === panelTab || entry.target === panelContent) {
          onResize()
        }
      }
    })
    
    resizeObserver.value.observe(panelTab)
    
    // Also observe panel content if it exists
    if (panelContent) {
      resizeObserver.value.observe(panelContent)
    }
  }

  // Initialize IntersectionObserver for off-screen detection
  function initializeIntersectionObserver(
    panelTab: HTMLElement | null,
    onVisibilityChange: (isVisible: boolean) => void
  ) {
    if (!panelTab) return
    
    intersectionObserver.value = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === panelTab) {
            isPanelOnScreen.value = entry.isIntersecting
            onVisibilityChange(entry.isIntersecting)
          }
        }
      },
      {
        // Add some margin to detect when panel is about to enter/leave
        rootMargin: PANEL_CONFIG.INTERSECTION_ROOT_MARGIN,
        threshold: 0
      }
    )
    
    intersectionObserver.value.observe(panelTab)
  }

  // Cleanup observers
  function cleanupObservers() {
    // Clean up ResizeObserver
    if (resizeObserver.value) {
      resizeObserver.value.disconnect()
      resizeObserver.value = null
    }
    
    // Clean up IntersectionObserver
    if (intersectionObserver.value) {
      intersectionObserver.value.disconnect()
      intersectionObserver.value = null
    }
    
    // Reset visibility state
    isPanelOnScreen.value = true
  }

  // Initialize all performance optimizations
  function initializePerformanceOptimizations(
    panelTab: HTMLElement | null,
    panelContent: HTMLElement | null,
    onResize: () => void,
    onVisibilityChange: (isVisible: boolean) => void
  ) {
    // Small delay to ensure panel is fully positioned
    setTimeout(() => {
      initializeResizeObserver(panelTab, panelContent, onResize)
      initializeIntersectionObserver(panelTab, onVisibilityChange)
    }, PANEL_CONFIG.OBSERVER_INIT_DELAY)
  }

  // Cleanup on component unmount
  onBeforeUnmount(cleanupObservers)

  return {
    // State
    isPanelOnScreen,
    
    // Methods
    getDynamicTransform,
    initializePerformanceOptimizations,
    cleanupObservers
  }
} 